import { DEX_TYPE } from '@/config/constants';

function resolveEnv(host) {
	const environments = ['dev', 'test', 'uat', 'pre-prod'];
	const hostParts = host.split('.');

	if (hostParts.some(part => !/^[a-zA-Z0-9-_]+$/.test(part))) {
		return null;
	}

	const envMatches = hostParts.filter(part =>
		environments.some(env => part === env)
	);

	if (envMatches.length > 1) {
		return null;
	}

	for (const env of environments) {
		const envPart = hostParts.find(part => part === env);
		if (envPart) {
			return env;
		}
	}

	const hasInvalidEnvPart = hostParts.some(part =>
		environments.some(env =>
			(part.toLowerCase().includes(env) || env.includes(part.toLowerCase())) &&
			part !== env
		)
	);

	return hasInvalidEnvPart ? null : 'prod';
}

function resolveDex(host) {
	if (host.includes('sgbuildex')) return 'sgbuildex';
	if (host.includes('sghealthdex')) return 'sghealthdex';
	if (host.includes('sgtradex.io') || host.includes('afa-cdi.com')) return 'sgtradex';
	return null;
}

// Unified-portal hostnames (e.g. `unified-portal.dev.dextech.ai`) don't carry
// the dex in the hostname — portal writes it to sessionStorage when the user
// picks a brand tile. Read that first; fall back to hostname matching for
// direct legacy URL access.
function resolveDexFromStorage() {
	if (typeof window === 'undefined' || !window.sessionStorage) return null;
	return window.sessionStorage.getItem('selectedDex');
}

// Cold email deep-links under the unified portal (e.g.
// `/admin/reset-password?token=…&dex=sgtradex`) carry the dex in the query
// string: the link is clicked with no session yet, and the unified-portal
// hostname doesn't encode the dex. Read it (only when shell-hosted) and persist
// it to sessionStorage so the rest of the app — theme resolution and the `dex`
// request header — resolves the same dex the email was issued for.
const KNOWN_DEXES = Object.values(DEX_TYPE);
function resolveDexFromQuery() {
	if (typeof window === 'undefined' || process.env.REACT_APP_SHELL_HOSTED !== 'true') return null;
	const dex = new URLSearchParams(window.location.search).get('dex');
	if (!dex || !KNOWN_DEXES.includes(dex)) return null;
	if (window.sessionStorage) window.sessionStorage.setItem('selectedDex', dex);
	return dex;
}

export function getEnvAndDex() {
	const host = window.location.hostname;

	let env, dex;

	if (host === 'localhost') {
		env = process.env.REACT_APP_CDI_ENV ?? process.env.REACT_APP_CDI_ENV;
		dex = resolveDexFromStorage() || resolveDexFromQuery() || process.env.REACT_APP_DEX_ENV;
	} else {
		env = resolveEnv(host);
		dex = resolveDexFromStorage() || resolveDexFromQuery() || resolveDex(host);
	}

	if (!env || !dex) {
		throw new Error(`Unable to determine env or dex from hostname: ${host}`);
	}

	return { env, dex };
}
